import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../components/auth';
import { Card, CardContent, Button } from '../components/ui';
import './HomePage.css';

export const HomePage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();

  return (
    <div className="home-page">
      <div className="home-container">
        <main>


          <div id="main-content">
            {/* Hero Section */}
            <section className="hero">
              <h1 className="hero-title">
                Welcome to My Nerdy Son
              </h1>
              <p className="hero-subtitle">
                Your friendly technology helper, always ready to assist with patient, 
                step-by-step guidance for all your tech questions.
              </p>
              
              {!isAuthenticated ? (
                <div className="hero-actions">
                  <Link to="/auth/signup">
                    <Button size="lg">Get Started</Button>
                  </Link>
                  <Link to="/auth/signin">
                    <Button variant="outline" size="lg">Sign In</Button>
                  </Link>
                </div>
              ) : (
                <div className="hero-actions">
                  <Link to="/chat">
                    <Button size="lg">Start Chatting</Button>
                  </Link>
                  <Link to="/knowledge-base">
                    <Button variant="outline" size="lg">Browse Help Topics</Button>
                  </Link>
                </div>
              )}
            </section>

            {/* Welcome Back Section for Authenticated Users */}
            {isAuthenticated && user && (
              <section className="welcome-back">
                <Card variant="elevated" padding="lg">
                  <CardContent>
                    <h2>Welcome back{user.givenName ? `, ${user.givenName}` : ''}!</h2>
                    <p>
                      I'm here to help you with any technology questions you might have. 
                      Whether it's troubleshooting your email, understanding a new app, 
                      or setting up a device, I'm ready to guide you through it step by step.
                    </p>
                  </CardContent>
                </Card>
              </section>
            )}

            {/* Features Section */}
            <section className="features-section">
              <h2 className="features-title">
                How I Can Help You
              </h2>
              
              <div className="features-grid">
                <Card variant="outlined" padding="lg" className="feature-card">
                  <CardContent>
                    <h3>📧 Email Help</h3>
                    <p>
                      Get help with sending emails, organizing your inbox, 
                      dealing with spam, and setting up email on new devices.
                    </p>
                  </CardContent>
                </Card>

                <Card variant="outlined" padding="lg" className="feature-card">
                  <CardContent>
                    <h3>🖨️ Printer Support</h3>
                    <p>
                      Troubleshoot printing problems, connect wireless printers, 
                      and learn how to scan documents.
                    </p>
                  </CardContent>
                </Card>

                <Card variant="outlined" padding="lg" className="feature-card">
                  <CardContent>
                    <h3>📱 Device Setup</h3>
                    <p>
                      Set up new phones, tablets, or computers with step-by-step 
                      guidance that's easy to follow.
                    </p>
                  </CardContent>
                </Card>

                <Card variant="outlined" padding="lg" className="feature-card">
                  <CardContent>
                    <h3>🌐 Internet & Apps</h3>
                    <p>
                      Learn how to use websites, download apps safely, 
                      and navigate the internet with confidence.
                    </p>
                  </CardContent>
                </Card>

                <Card variant="outlined" padding="lg" className="feature-card">
                  <CardContent>
                    <h3>🔒 Security & Safety</h3>
                    <p>
                      Stay safe online with tips on passwords, avoiding scams, 
                      and protecting your personal information.
                    </p>
                  </CardContent>
                </Card>

                <Card variant="outlined" padding="lg" className="feature-card">
                  <CardContent>
                    <h3>💬 Patient Guidance</h3>
                    <p>
                      Ask questions in plain English and get clear, patient 
                      explanations without technical jargon.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </section>

            {/* Example Questions Section */}
            <section className="examples-section">
              <Card variant="default" padding="lg">
                <CardContent>
                  <h2 className="examples-title">
                    Example Questions I Can Help With
                  </h2>
                  
                  <div className="examples-grid">
                    <div className="examples-category">
                      <h4>Email Questions</h4>
                      <ul className="examples-list">
                        <li>"How do I attach a photo to an email?"</li>
                        <li>"Why aren't my emails sending?"</li>
                        <li>"How do I delete old emails?"</li>
                        <li>"How do I organize my inbox?"</li>
                      </ul>
                    </div>
                    
                    <div className="examples-category">
                      <h4>Device Help</h4>
                      <ul className="examples-list">
                        <li>"My tablet won't connect to WiFi"</li>
                        <li>"How do I make the text bigger?"</li>
                        <li>"The screen is too bright"</li>
                        <li>"How do I take a screenshot?"</li>
                      </ul>
                    </div>
                    
                    <div className="examples-category">
                      <h4>Safety Concerns</h4>
                      <ul className="examples-list">
                        <li>"Is this email a scam?"</li>
                        <li>"How do I create a strong password?"</li>
                        <li>"Someone called about my computer"</li>
                        <li>"How do I protect my personal information?"</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Testimonials Section */}
            {!isAuthenticated && (
              <section className="testimonials-section">
                <h2 className="testimonials-title">
                  What People Are Saying
                </h2>
                
                <div className="testimonials-grid">
                  <div className="testimonial-card">
                    <p className="testimonial-quote">
                      I was so frustrated with my email until I found My Nerdy Son. 
                      The explanations are so clear and patient. I finally understand 
                      how to organize my inbox and send photos to my grandchildren!
                    </p>
                    <p className="testimonial-author">— Margaret, 72</p>
                  </div>

                  <div className="testimonial-card">
                    <p className="testimonial-quote">
                      This is exactly what I needed! No confusing technical terms, 
                      just simple step-by-step help. I've learned more about my 
                      tablet in a week than I did in months of trying on my own.
                    </p>
                    <p className="testimonial-author">— Robert, 68</p>
                  </div>

                  <div className="testimonial-card">
                    <p className="testimonial-quote">
                      The security advice saved me from a phone scam! I feel so much 
                      more confident using the internet now. It's like having a 
                      knowledgeable grandson who's always available to help.
                    </p>
                    <p className="testimonial-author">— Dorothy, 75</p>
                  </div>
                </div>
              </section>
            )}

            {/* Trust Indicators */}
            {!isAuthenticated && (
              <section className="trust-section">
                <h3 className="trust-title">Why Choose My Nerdy Son?</h3>
                <div className="trust-indicators">
                  <div className="trust-item">
                    <span className="trust-icon">✓</span>
                    <span>100% Free to Use</span>
                  </div>
                  <div className="trust-item">
                    <span className="trust-icon">✓</span>
                    <span>No Technical Jargon</span>
                  </div>
                  <div className="trust-item">
                    <span className="trust-icon">✓</span>
                    <span>Patient & Understanding</span>
                  </div>
                  <div className="trust-item">
                    <span className="trust-icon">✓</span>
                    <span>Available 24/7</span>
                  </div>
                  <div className="trust-item">
                    <span className="trust-icon">✓</span>
                    <span>Secure & Private</span>
                  </div>
                </div>
              </section>
            )}

            {/* Call to Action */}
            {!isAuthenticated && (
              <section className="cta-section">
                <h2 className="cta-title">Ready to Get Started?</h2>
                <p className="cta-description">
                  Create your free account and start getting the tech help you need. 
                  No credit card required, no complicated setup.
                </p>
                <Link to="/auth/signup">
                  <Button size="xl">Create Your Account</Button>
                </Link>
              </section>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};